import type { UseQueryResult } from '@tanstack/react-query'
import type { LinkProps } from '@tanstack/react-router'
import type { ComponentType, ReactNode } from 'react'

import { COMMON } from '@brickup/shared/locales/de/common'
import { MATCHING } from '@brickup/shared/locales/de/matching'
import { NAV } from '@brickup/shared/locales/de/nav'
import { STAFF_DASHBOARD } from '@brickup/shared/locales/de/staff-dashboard'
import { TERMS } from '@brickup/shared/locales/de/terms'
import { formatDate, formatDateTime } from '@brickup/shared/utils'
import { DashboardPanel } from './DashboardLayout'
import { useBackupOverview } from '@/api/backup'
import { useEmailStats } from '@/api/email-management'
import { GLANCE } from '@/api/glance'
import { useQueueStats } from '@/api/hubspot-queue'
import { useHubspotSyncConfig } from '@/api/hubspot-sync'
import { useLogStats } from '@/api/logs'
import { useRobotStatus } from '@/api/matching'
import { useReprocessStatus, useResolutionReport } from '@/api/media'
import { Badge, dash, type Tone } from '@/components/base'
import { LabelValue } from '@/components/Internal/LabelValue'
import { LabelValueGroup } from '@/components/Internal/LabelValueGroup'
import { mayOpen } from '@/constants/navigation-permissions'
import { selectUserRoles, useAppStore } from '@/stores'

const COPY = STAFF_DASHBOARD.system

/**
 * The counts are glanced at here, not watched: once a minute, and not again
 * on every return to the tab. Some of these endpoints read a whole log file
 * or list a bucket, and six of them answer this one panel.
 */
type State = keyof typeof COPY.states

/** The page a tile leads to, and whose endpoint it reads. */
type TileProps = { path: NonNullable<LinkProps['to']> }

const STATE_TONE = {
  ok: 'success',
  check: 'warning',
  failed: 'error',
  off: 'default',
} as const satisfies Record<State, Tone>

/**
 * One part of the running system: its figure, what the figure means, and a
 * pill saying whether it is well. A part whose endpoint fails says so — the
 * one thing a health view must never show as "still loading".
 */
function HealthTile({
  label,
  path,
  queries,
  value,
  hint,
  state,
}: {
  label: string
  path: TileProps['path']
  /** Every query the tile reads; it is ready once all have answered. */
  queries: UseQueryResult[]
  value: ReactNode
  hint?: ReactNode
  state: State
}) {
  const failed = queries.some(query => query.isError)
  // A figure outlives a failed refetch, as every gated region does; the pill
  // is what says the last read did not answer.
  const ready = queries.every(query => query.data !== undefined)
  const shown: State | null = failed ? 'failed' : ready ? state : null

  return (
    <LabelValue
      label={label}
      value={ready ? value : dash}
      hint={ready ? hint : undefined}
      status={
        shown ? (
          <Badge
            size="sm"
            variant={STATE_TONE[shown]}
            label={COPY.states[shown]}
          />
        ) : null
      }
      to={{ to: path }}
    />
  )
}

function EmailTile({ path }: TileProps) {
  const query = useEmailStats(GLANCE)
  const day = query.data?.last24Hours
  return (
    <HealthTile
      label={TERMS.email}
      path={path}
      queries={[query]}
      value={day?.failed}
      hint={COPY.emailHint(day?.sent ?? 0)}
      state={day?.failed ? 'failed' : 'ok'}
    />
  )
}

function HubspotTile({ path }: TileProps) {
  const sync = useHubspotSyncConfig(GLANCE)
  const queue = useQueueStats(GLANCE)
  const failed = queue.data?.failed ?? 0
  return (
    <HealthTile
      label={NAV.hubspot}
      path={path}
      queries={[sync, queue]}
      value={failed}
      hint={COPY.hubspotHint(
        sync.data?.enabled ? formatDateTime(sync.data.lastRun) : null,
      )}
      state={failed > 0 ? 'check' : sync.data?.enabled ? 'ok' : 'off'}
    />
  )
}

function RobotTile({ path }: TileProps) {
  const query = useRobotStatus(GLANCE)
  const robot = query.data
  return (
    <HealthTile
      label={MATCHING.robot.title}
      path={path}
      queries={[query]}
      value={robot?.enabled ? COMMON.enabled : COMMON.disabled}
      hint={
        robot?.nextRun
          ? STAFF_DASHBOARD.nextRun(formatDateTime(robot.nextRun))
          : undefined
      }
      state={robot?.enabled ? 'ok' : 'off'}
    />
  )
}

function LogsTile({ path }: TileProps) {
  const query = useLogStats(GLANCE)
  const errors = (query.data?.error ?? 0) + (query.data?.fatal ?? 0)
  return (
    <HealthTile
      label={NAV.logs}
      path={path}
      queries={[query]}
      value={errors}
      hint={COPY.logsHint(query.data?.warn ?? 0)}
      state={errors > 0 ? 'check' : 'ok'}
    />
  )
}

function ImagesTile({ path }: TileProps) {
  const reprocess = useReprocessStatus(GLANCE)
  const report = useResolutionReport(GLANCE)
  const pending = reprocess.data?.pending ?? 0
  const tooSmall = report.data?.length ?? 0
  return (
    <HealthTile
      label={NAV.imagePipeline}
      path={path}
      queries={[reprocess, report]}
      value={pending}
      hint={COPY.imagesHint(reprocess.data?.running ?? false, tooSmall)}
      state={pending > 0 || tooSmall > 0 ? 'check' : 'ok'}
    />
  )
}

function BackupTile({ path }: TileProps) {
  const query = useBackupOverview(GLANCE)
  const archive = query.data?.archive
  return (
    <HealthTile
      label={NAV.backup}
      path={path}
      queries={[query]}
      value={archive ? formatDate(archive.createdAt) : COPY.backupNone}
      hint={query.data?.running ? COPY.backupRunning : undefined}
      state={archive ? 'ok' : 'check'}
    />
  )
}

/** Each tile with the page it leads to. A tile is shown to whoever may open
 *  that page — the same roles its endpoint admits — so none needs a role
 *  check of its own. */
const TILES: (TileProps & { Tile: ComponentType<TileProps> })[] = [
  { path: '/internal/system/operations/email', Tile: EmailTile },
  { path: '/internal/system/operations/queue', Tile: HubspotTile },
  { path: '/internal/matching', Tile: RobotTile },
  { path: '/internal/system/operations/logs', Tile: LogsTile },
  { path: '/internal/system/settings/images', Tile: ImagesTile },
  { path: '/internal/system/operations/backup', Tile: BackupTile },
]

/**
 * Whether the running system is well, one tile per part, each read from the
 * endpoint its own page already uses and leading there.
 */
export function SystemHealth() {
  const roles = useAppStore(selectUserRoles)

  return (
    <DashboardPanel title={COPY.health}>
      <LabelValueGroup variant="tiles">
        {TILES.filter(({ path }) => mayOpen(roles, path)).map(
          ({ path, Tile }) => (
            <Tile key={path} path={path} />
          ),
        )}
      </LabelValueGroup>
    </DashboardPanel>
  )
}
